import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskLine, FadeUp } from "./Motion";

export const Hero = ({ onScrollTo }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);

  return (
    <section id="top" ref={ref} className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      {/* headline */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 items-end">
        <motion.div
          style={reduced ? {} : { y: yText }}
          className="col-span-12 lg:col-span-8"
        >
          <h1
            data-testid="hero-headline"
            className="font-display text-[13vw] md:text-[9vw] lg:text-[7.2vw] text-ink leading-[0.92]"
          >
            <span className="block">
              <MaskLine delay={0}>Beyond</MaskLine>{" "}
              <MaskLine delay={1}>Credit</MaskLine>{" "}
              <MaskLine delay={2}>History<span className="text-teal">.</span></MaskLine>
            </span>
            <span className="block italic text-ink/85 mt-2">
              <MaskLine delay={3}>A true measure</MaskLine>
            </span>
            <span className="block italic text-ink/85">
              <MaskLine delay={4}>of business health.</MaskLine>
            </span>
          </h1>
        </motion.div>

        <div className="col-span-12 lg:col-span-4 lg:pb-4">
          <FadeUp delay={0.9}>
            <p className="font-sans text-base md:text-lg text-ink/70 leading-relaxed max-w-md">
              Credablr combines verified financial data, GST compliance
              records, commercial credit history, and trading relationships
              into a standardised Business Health Report — helping NBFCs and
              banks make faster, more accurate MSME lending decisions.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="hero-cta-demo"
                onClick={() => onScrollTo?.("contact")}
                className="btn-primary"
              >
                Request a Demo <span aria-hidden>→</span>
              </button>
              <button
                data-testid="hero-cta-how"
                onClick={() => onScrollTo?.("how")}
                className="btn-ghost"
              >
                See How It Works
              </button>
            </div>
          </FadeUp>
        </div>
      </div>

      {/* Hero visual — architectural building with parallax */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 mt-14 md:mt-20">
        <FadeUp delay={0.5}>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-stretch">
            <div className="col-span-12 md:col-span-8 relative">
              <div className="bento-img aspect-[16/9] md:aspect-[16/8] relative">
                <motion.img
                  src="https://images.pexels.com/photos/10958528/pexels-photo-10958528.jpeg"
                  alt="Financial institution architecture"
                  className="absolute inset-0 w-full h-[115%] object-cover"
                  style={reduced ? {} : { y: yImg, scale: scaleImg }}
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
                <div className="absolute left-4 md:left-6 bottom-4 md:bottom-6 text-paper">
                  <div className="font-mono-cap text-paper/80">Fig. 01 / Institutional Trust</div>
                </div>
                <div className="absolute right-4 md:right-6 top-4 md:top-6 text-paper">
                  <div className="font-mono-cap text-paper/80">Mumbai · 2026</div>
                </div>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4 grid grid-rows-2 gap-6 md:gap-10">
              <div className="bento-img aspect-square md:aspect-auto relative">
                <img
                  src="https://images.pexels.com/photos/38178433/pexels-photo-38178433.jpeg"
                  alt="MSME artisan"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="pillar-card--dark p-6 md:p-8 flex flex-col justify-between">
                <div className="font-mono-cap text-paper/70">Positioning</div>
                <p className="font-display italic text-2xl md:text-3xl text-paper leading-tight">
                  Making invisible <br />businesses <span className="text-teal">visible</span>.
                </p>
                <div className="flex items-center justify-between font-mono-cap text-paper/60">
                  <span>— Manifesto</span>
                  <span>MHS</span>
                </div>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
